import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';

interface LegalSection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
  contactLink?: boolean;
}

@Component({
  selector: 'app-terms',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './terms.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./terms.scss']
})
export class Terms {
  readonly lastUpdated = '20 September 2026';

  readonly sections: LegalSection[] = [
    {
      id: 'acceptance',
      title: '1. Acceptance of these Terms',
      paragraphs: [
        'These Terms of Service ("Terms") govern your use of everyrupeespent.com and all services offered on it, including our free financial calculators, the AI Advisor feature, the Reminders application, the Budget & Expense Tracker, the Rental Management System, and the Rental Agreement Generator (together, the "Services"), operated by Every Rupee Spent ("we", "us", "our").',
        'By using the Services, creating an account, or making a payment, you agree to these Terms. If you don\'t agree, please don\'t use the Services.'
      ]
    },
    {
      id: 'services-overview',
      title: '2. What we offer',
      paragraphs: ['The Services fall into the following categories, each with different terms that apply to it:'],
      bullets: [
        'Free calculators (Home Loan, SIP, Income Tax and others) and the optional AI Advisor feature — free to use, no account required.',
        'Reminders — a bill and deadline tracking application with a free tier and paid annual subscription plans.',
        'Budget & Expense Tracker — a personal budgeting and expense-logging application with a free tier and a paid annual plan.',
        'Rental Management System — a property, tenant, lease, rent-collection and maintenance-tracking application for landlords, with a free tier, paid annual plans, and a self-serve portal for tenants.',
        'Rental Agreement Generator — a paid, one-time document-generation tool that produces a form-filled rental agreement.'
      ]
    },
    {
      id: 'not-advice',
      title: '3. Calculators and AI Advisor are informational only',
      paragraphs: [
        'Our calculators are provided for general informational and educational purposes only. They rely on the inputs you provide and standard formulas, and are not a substitute for professional financial, tax, investment or legal advice.',
        'Where a calculator offers an "AI Advisor" feature, the observations shown are generated by an AI model based on your inputs, in real time, without human review before being shown to you. Treat AI Advisor output as a starting point for your own research, not as personalized advice. You should independently verify any figures before making a financial decision, and consult a qualified professional (a chartered accountant, financial advisor or tax consultant) where the decision is significant.',
        'We do not guarantee the accuracy, completeness or applicability of any calculator result or AI Advisor output to your specific situation.'
      ]
    },
    {
      id: 'budget-service',
      title: '4. Budget & Expense Tracker — scope and limitations',
      paragraphs: [
        'The Budget & Expense Tracker lets you set monthly budgets and log expenses to track your own spending. It is a record-keeping and planning tool, not a bank connection — it does not access your bank accounts, cards or transaction history, and every entry is one you type in yourself.',
        'A basic tier is free to use with an account; per-category budgets and full history require an active paid annual plan. We are not responsible for spending decisions you make based on the summaries and reports shown in the app.'
      ]
    },
    {
      id: 'rms-service',
      title: '5. Rental Management System — scope and limitations',
      paragraphs: [
        'The Rental Management System helps landlords track properties, tenants, leases, rent payments, maintenance requests and related documents, and gives tenants a self-serve portal to view their lease and payment history. It is a record-keeping and coordination tool — recording a rent payment in the app does not itself transfer money, and we are not a party to, and do not guarantee, any lease or agreement between a landlord and tenant.',
        'A free tier covers a single property; paid annual plans raise the property limit and unlock maintenance tracking, tenant self-service and other features. You are responsible for the accuracy of the property, tenant and lease information you enter, and for complying with applicable rental and tenancy law in your jurisdiction.'
      ]
    },
    {
      id: 'rental-agreement-service',
      title: '6. Rental Agreement Generator — scope and limitations',
      paragraphs: [
        'The Rental Agreement Generator produces a form-filled, 11-month Karnataka rental agreement based on the details you provide. It is a document-drafting convenience, not legal advice, and does not substitute for review by a qualified advocate — particularly for agreements outside standard 11-month leave-and-licence terms, or where the parties\' circumstances are unusual.',
        'The optional e-Stamp add-on purchases Karnataka e-Stamp paper for the agreement through our e-Stamp vendor; the stamp duty amount is a pass-through set by the Karnataka government, plus a ₹150 handling fee. It does not include legal drafting, negotiation or advice beyond the standard template, and you remain responsible for verifying that the details you enter are accurate and for registering the agreement as required by law.'
      ]
    },
    {
      id: 'ai-use',
      title: '7. Our use of artificial intelligence',
      paragraphs: [
        'We use AI tools as part of certain features, and want to be upfront about where and how:'
      ],
      bullets: [
        'AI Advisor (calculators): output is generated directly by an AI model from your inputs and shown to you without human review. See Section 3.'
      ]
    },
    {
      id: 'accounts',
      title: '8. Accounts',
      paragraphs: [
        'Some features (Reminders, Budget & Expense Tracker, Rental Management System, billing) require an account. You are responsible for keeping your login credentials confidential and for all activity under your account. Let us know immediately if you suspect unauthorized access.'
      ]
    },
    {
      id: 'payments',
      title: '9. Payments and pricing',
      paragraphs: [
        'Paid Services are billed in Indian Rupees (₹) for customers in India, or US Dollars ($) for customers elsewhere, and processed securely through Razorpay. We do not store your card or payment credentials — they are handled entirely by Razorpay.',
        'The Rental Agreement Generator is a one-time payment — there is no subscription or auto-renewal. Reminders, Budget & Expense Tracker and Rental Management System premium plans are annual subscriptions that renew automatically unless cancelled before the renewal date; you can cancel anytime from your account\'s Billing page, effective at the end of the current billing period.'
      ]
    },
    {
      id: 'refunds',
      title: '10. Refunds and cancellations',
      paragraphs: [
        'Rental Agreement Generator: because work begins as soon as payment is confirmed, this one-time payment is non-refundable once processing has started. If a technical issue on our end prevents us from delivering the document at all, we will issue a full refund.',
        'Reminders, Budget & Expense Tracker and Rental Management System premium subscriptions: you can cancel auto-renewal at any time; cancellation stops future renewals but does not refund the current billing period already paid for.',
        'If you believe you\'ve been charged in error, contact us at the email in Section 19 and we\'ll look into it.',
        'See our Refund & Cancellation Policy for a plain-language summary of this section.'
      ]
    },
    {
      id: 'plan-changes',
      title: '11. Upgrading and downgrading a paid plan',
      paragraphs: [
        'Reminders and the Rental Management System let you switch between plan tiers at any time. Upgrading is always immediate. Downgrading — including cancelling a paid plan back to the free tier — is also allowed, but if you currently hold more items than the plan you\'re moving to allows (for example, more properties than a lower Rental Management System tier\'s limit, or more active reminders than a lower Reminders tier\'s quota), you must choose which excess items to archive before the switch takes effect. We will not pick for you, and we will not silently delete anything.',
        'Archiving is a soft delete: the archived item is hidden from your active lists, dashboards and reports and stops generating reminders or notifications, but its data is kept for up to 12 months so you can recover it by upgrading again. After 12 months, archived data may be permanently deleted.',
        'This rule exists so that a temporary upgrade cannot be used to permanently exceed a lower tier\'s limits — you cannot upgrade, add items beyond a cheaper plan\'s cap, and then downgrade while keeping full, unrestricted use of everything you added. Once you\'ve reconciled your usage to the new plan\'s limits, the downgrade proceeds immediately at your choice; there is no other way to bypass a plan\'s limits.'
      ]
    },
    {
      id: 'ip',
      title: '12. Intellectual property',
      paragraphs: [
        'The Services, including their design, code, calculators and content, are owned by Every Rupee Spent and protected by applicable intellectual property laws. Your generated rental agreement is yours to use once delivered. You may not resell, redistribute or scrape the Services or represent them as your own.'
      ]
    },
    {
      id: 'no-warranty',
      title: '13. No warranty',
      paragraphs: [
        'The Services are provided "as is" and "as available", without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Services will be uninterrupted, error-free, or produce any particular result.'
      ]
    },
    {
      id: 'liability',
      title: '14. Limitation of liability',
      paragraphs: [
        'To the maximum extent permitted by law, Every Rupee Spent will not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits, revenue, data or business opportunity, arising from your use of the Services — including calculator results, AI Advisor output, the Budget & Expense Tracker, the Rental Management System, the Rental Agreement Generator, or changes you make based on our recommendations or generated documents.',
        'Where liability cannot be excluded by law, our total liability for any claim arising out of these Terms is limited to the amount you paid us for the specific Service giving rise to the claim in the 3 months before the claim.'
      ]
    },
    {
      id: 'indemnity',
      title: '15. Indemnification',
      paragraphs: [
        'You agree to indemnify and hold Every Rupee Spent harmless from any claims, losses or expenses (including reasonable legal fees) arising from your use of the Services, your violation of these Terms, or content you submit to us (such as the property, tenant and lease details you enter into the Rental Management System).'
      ]
    },
    {
      id: 'privacy',
      title: '16. Privacy',
      paragraphs: [
        'We describe in full how we collect, use and protect your personal data — including account data for Reminders, the Budget & Expense Tracker and the Rental Management System — in our Privacy Policy. This section is a summary; the Privacy Policy governs.',
        'We do not sell your personal data.'
      ]
    },
    {
      id: 'termination',
      title: '17. Termination',
      paragraphs: [
        'We may suspend or terminate your access to the Services if you violate these Terms, misuse the Services, or engage in fraudulent or abusive activity. You may stop using the Services, or delete your account, at any time.'
      ]
    },
    {
      id: 'changes',
      title: '18. Changes to these Terms',
      paragraphs: [
        'We may update these Terms from time to time as the Services evolve. We\'ll update the "Last updated" date above when we do. Continuing to use the Services after a change means you accept the updated Terms.'
      ]
    },
    {
      id: 'governing-law',
      title: '19. Governing law',
      paragraphs: [
        'These Terms are governed by the laws of India. Any disputes arising from these Terms or the Services will be subject to the exclusive jurisdiction of the competent courts in India.'
      ]
    },
    {
      id: 'contact',
      title: '20. Contact us',
      paragraphs: [
        'Questions about these Terms, a specific Service, billing, or a request for correction — write to us and we\'ll get back to you within a reasonable time, generally within 15 business days.'
      ],
      contactLink: true
    }
  ];
}
